import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import {
  BookOpen, FileText, GraduationCap, BarChart3, ScrollText,
  Lightbulb, PenTool, Code2, TrendingUp, LineChart, Database,
  Shield, Rocket, Target, ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Block = { icon: LucideIcon; title: string; body: string };
type ChildPage = {
  title: string;
  eyebrow: string;
  sub: string;
  metaDesc: string;
  intro: string;
  blocks: Block[];
  cta?: { label: string; to: string };
};

const CHILDREN: Record<string, ChildPage> = {
  "blog": {
    eyebrow: "Resources · Learn",
    title: "Blog",
    sub: "Field notes from the people building the work.",
    metaDesc: "The CoderLity blog — engineering, design, growth and operations writing from the team shipping the work.",
    intro:
      "Short-form writing on what we're learning while shipping software, SaaS and growth systems. No fluff, no listicles — just notes that would have helped us last quarter.",
    blocks: [
      { icon: Code2, title: "Engineering", body: "TypeScript, React, infra and DevOps notes from production engagements." },
      { icon: PenTool, title: "Design", body: "Product design process, motion, accessibility and brand systems." },
      { icon: TrendingUp, title: "Growth", body: "Performance marketing, SEO, lifecycle and CRO experiments — wins and misses." },
      { icon: Lightbulb, title: "Ops & culture", body: "How a small senior team runs multi-stream engagements without burning out." },
    ],
  },
  "guides": {
    eyebrow: "Resources · Learn",
    title: "Guides",
    sub: "In-depth, opinionated playbooks.",
    metaDesc: "Long-form CoderLity guides on building, scaling and growing software products — opinionated and battle-tested.",
    intro:
      "Longer than a post, shorter than a book. Each guide distills how we approach a specific problem — from picking a stack to instrumenting growth — with the trade-offs we'd actually defend.",
    blocks: [
      { icon: Rocket, title: "Launching a SaaS", body: "From idea to first paying customer — stack, pricing, onboarding and analytics." },
      { icon: Database, title: "Choosing a stack", body: "TanStack Start, Supabase, Cloudflare — when to pick what, and what to avoid." },
      { icon: Shield, title: "Production hardening", body: "Auth, RLS, observability and incident response for serious products." },
      { icon: Target, title: "Growth foundations", body: "Tracking, attribution, lifecycle and a healthy paid + organic mix." },
    ],
  },
  "tutorials": {
    eyebrow: "Resources · Learn",
    title: "Tutorials",
    sub: "Step-by-step, runnable walkthroughs.",
    metaDesc: "Hands-on CoderLity tutorials — runnable code, real stacks, no abandoned half-finished examples.",
    intro:
      "Hands-on walkthroughs you can actually finish in a sitting. Real stacks, real code, no abandoned half-built examples — every tutorial ends with something you can deploy.",
    blocks: [
      { icon: Code2, title: "Full-stack TanStack Start", body: "Build, auth, deploy — end to end on Cloudflare with Supabase." },
      { icon: Database, title: "Supabase + RLS", body: "Modeling roles, policies and admin tooling without footguns." },
      { icon: PenTool, title: "Motion in React", body: "Practical Motion / GSAP recipes for product UI — not just landing pages." },
      { icon: TrendingUp, title: "Analytics that ship", body: "Wire up product, marketing and revenue events in one afternoon." },
    ],
  },
  "industry-reports": {
    eyebrow: "Resources · Reports",
    title: "Industry Reports",
    sub: "Original research on the markets we work in.",
    metaDesc: "CoderLity's original industry research — benchmarks and trends across SaaS, e-commerce, fintech and healthcare.",
    intro:
      "Original data, not recycled stats. Each report aggregates anonymized benchmarks from our engagements plus structured interviews with operators in the field.",
    blocks: [
      { icon: LineChart, title: "SaaS Benchmarks", body: "Onboarding, activation, retention and pricing across 80+ B2B SaaS products." },
      { icon: BarChart3, title: "E-commerce CRO", body: "Conversion benchmarks by category, traffic source and AOV band." },
      { icon: TrendingUp, title: "Fintech Trust", body: "What actually moves trust and conversion for regulated fintech products." },
      { icon: Shield, title: "Healthcare UX", body: "Patient-facing UX patterns that meet compliance without killing conversion." },
    ],
    cta: { label: "Request a report", to: "/contact" },
  },
  "whitepapers": {
    eyebrow: "Resources · Reports",
    title: "Whitepapers",
    sub: "Technical deep dives, citation-friendly.",
    metaDesc: "CoderLity whitepapers — technical deep dives on architecture, security and performance for serious teams.",
    intro:
      "Longer technical pieces meant to be cited, shared with stakeholders and used in procurement. Versioned, peer-reviewed inside the team, and updated as the field moves.",
    blocks: [
      { icon: Shield, title: "Edge security model", body: "How we architect auth, RLS and abuse prevention on Cloudflare + Supabase." },
      { icon: Database, title: "Multi-tenant data", body: "Patterns for SaaS data isolation, billing-aware schemas and noisy-neighbor control." },
      { icon: Rocket, title: "Performance budgets", body: "Practical Core Web Vitals budgets, with measurement and enforcement." },
      { icon: ScrollText, title: "AI in production", body: "Cost, latency, evals and guardrails for shipping LLM features users trust." },
    ],
    cta: { label: "Request a whitepaper", to: "/contact" },
  },
};

export const Route = createFileRoute("/resources/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/resources/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ResourcesChild,
});

function ResourcesChild() {
  const { item } = Route.useLoaderData();
  return (
    <PageShell>
      <PageHero eyebrow={item.eyebrow} title={item.title} sub={item.sub} />
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">{item.intro}</p>
        </div>
      </section>
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {item.blocks.map((b: Block) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </section>
      {item.cta && (
        <section className="py-12">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <Link
              to={item.cta.to}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow"
            >
              {item.cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}
      <CTABanner />
    </PageShell>
  );
}
